
import { ChevronDown, ChevronUp, GripVertical, Save } from "lucide-react";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MobileTooltipWrapper } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

export type ColumnId = "name" | "category" | "type" | "rating" | "complexity" | "filters" | "actions";

export interface Column {
  id: ColumnId;
  label: string;
  className?: string;
  draggable: boolean;
  sortable: boolean;
}

export type SortDirection = "asc" | "desc" | null;

export interface SortState {
  column: ColumnId | null;
  direction: SortDirection;
}

interface PartTableHeaderProps {
  columns: Column[];
  sortState: SortState;
  handleSort: (columnId: ColumnId) => void;
  handleDragStart: (columnId: ColumnId) => void;
  handleDragOver: (e: React.DragEvent<HTMLTableCellElement>, columnId: ColumnId) => void;
  handleDrop: (columnId: ColumnId) => void;
  saveColumnOrder?: () => void;
  isSaving?: boolean;
}

const PartTableHeader = ({
  columns,
  sortState,
  handleSort,
  handleDragStart,
  handleDragOver,
  handleDrop,
  saveColumnOrder,
  isSaving = false
}: PartTableHeaderProps) => {
  // Render sort indicator
  const renderSortIndicator = (columnId: ColumnId) => {
    if (sortState.column !== columnId) return null;
    return sortState.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  return <TableHeader>
      <TableRow>
        {columns.map(column => (
          <TableHead
            key={column.id}
            className={`relative ${column.className || ''}`}
            draggable={column.draggable}
            onDragStart={() => handleDragStart(column.id)}
            onDragOver={(e) => handleDragOver(e, column.id)}
            onDrop={() => handleDrop(column.id)}
          >
            {column.id === 'actions' ? (
              <div className="flex justify-center">
                <MobileTooltipWrapper content="Save column order">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-7 w-7 p-0 hover:bg-muted/20 rounded-md"
                    onClick={saveColumnOrder}
                    disabled={isSaving}
                  >
                    <Save size={16} className={`text-muted-foreground ${isSaving ? "animate-pulse" : ""}`} />
                    <span className="sr-only">Save column order</span>
                  </Button>
                </MobileTooltipWrapper>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                {column.draggable && (
                  <span className="cursor-grab">
                    <GripVertical size={16} />
                  </span>
                )}
                <div
                  onClick={() => column.sortable && handleSort(column.id)}
                  className={column.sortable ? "cursor-pointer flex items-center gap-1" : ""}
                >
                  <MobileTooltipWrapper content={column.label}>
                    <span>{column.label}</span>
                  </MobileTooltipWrapper>
                  {renderSortIndicator(column.id)}
                </div>
              </div>
            )}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>;
};

export default PartTableHeader;
